import { stringify } from 'query-string';
import { HttpClient } from 'api/http-client';
import { Task } from 'api/tasks';
import { AllocatedMinutes } from 'components/organisms/resource-management/types';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { defaultFormat } from 'utils/date-field/formatter';
import { ApiGroupFilter } from 'utils/simple-search-filter/simple-search-filter';
import {
  AggregationMediumMinutes,
  AllocationBulkPayload,
  AllocationMedium,
  AllocationsGroupByTeam,
  AllocationsGroupByUser,
  AllocationsGroupByUsersOrTeams,
  AllocationSource,
  AvailabilityFilterApiWithPlanningLevel,
  AvailabilityForTeamWithProject,
  ConvertAllocationsPropsApi,
  DeleteAllocationsProps,
  FilteredResponseInPeople,
  FilteredResponseInProjects,
  FilteredRoleInPeople,
  FilteredUserInPeople,
  GetResourceAllocationDemandPayload,
  GetResourceAllocationPayload,
  PlanningLevelConfig,
  ResourceAllocationDemandResponse,
  ResourceManagementApiZoomType,
  ResourceManagementConfig,
  ResourceManagementExportParams,
  ResourceManagementFilterParams,
  ResourceManagementFilterResponse,
  ResourceManagementGroupBy,
  ResourceType,
  TeamLevelAggregation,
  UserLevelAggregation,
} from './types';

const baseUrl = '/resource-allocations';

export const getInitialAllocatedMinutes = (): AllocatedMinutes => ({
  [AllocationMedium.Hard]: 0,
  [AllocationMedium.Soft]: 0,
});

const computeAggregateMinutesSplit = (
  mediumMinutesTypes: AggregationMediumMinutes[],
): AllocatedMinutes =>
  mediumMinutesTypes.reduce(
    (result, { type, totalMinutes }) => ({
      ...result,
      [type]: totalMinutes,
    }),
    getInitialAllocatedMinutes(),
  );

export const getUserAggregationsFromApi = (
  aggregations: UserLevelAggregation[],
): UserLevelAggregation[] =>
  aggregations.map(
    ({ roleId, referenceTeamId, totalMinutes, mediumMinutes, ...rest }) => ({
      ...rest,
      ...(referenceTeamId
        ? {
            roleId,
            referenceTeamId,
          }
        : {}),
      type: ResourceType.User,
      allocatedMintutes: computeAggregateMinutesSplit(
        mediumMinutes?.types ?? [],
      ),
    }),
  );

export const getTeamAggregationsFromApi = (
  aggregations: TeamLevelAggregation[],
): TeamLevelAggregation[] =>
  aggregations.map(({ mediumMinutes, ...rest }) => ({
    ...rest,
    type: ResourceType.Team,
    allocatedMintutes: computeAggregateMinutesSplit(mediumMinutes?.types ?? []),
  }));

const addUserTypeToAllocation = (
  allocationByUser: AllocationsGroupByUser,
): AllocationsGroupByUser => ({
  ...allocationByUser,
  type: ResourceType.User,
  allocations: allocationByUser.allocations.map(
    ({ role, referenceTeam, ...rest }) => ({
      ...rest,
      ...(referenceTeam != null
        ? {
            referenceTeam,
            role,
          }
        : {}),
      type: ResourceType.User,
    }),
  ),
  aggregations: getUserAggregationsFromApi(allocationByUser.aggregations),
});
const addTeamTypeToAllocation = (
  allocationByTeam: AllocationsGroupByTeam,
): AllocationsGroupByTeam => ({
  ...allocationByTeam,
  type: ResourceType.Team,
  allocations: allocationByTeam.allocations.map((a) => ({
    ...a,
    type: ResourceType.Team,
  })),
  aggregations: getTeamAggregationsFromApi(allocationByTeam.aggregations),
});

const addUserTypeToFilteredResource = (
  filteredResources: FilteredUserInPeople,
): FilteredUserInPeople => ({
  ...filteredResources,
  type: ResourceType.User,
});

const addRoleTypeToFilteredResource = (
  filteredResources: FilteredRoleInPeople,
): FilteredRoleInPeople => ({
  ...filteredResources,
  type: ResourceType.Role,
});

class ResourceManagementClient extends HttpClient {
  public getResourceAllocations = async (
    zoomLevel: ResourceManagementApiZoomType,
    payload: GetResourceAllocationPayload,
  ): Promise<AllocationsGroupByUsersOrTeams> =>
    this.post(`${baseUrl}/lite?zoomLevel=${zoomLevel}`, payload);

  public getResourceAllocationsForPeople = async (
    zoomLevel: ResourceManagementApiZoomType,
    payload: GetResourceAllocationPayload,
  ): Promise<AllocationsGroupByUsersOrTeams> =>
    this.getResourceAllocations(zoomLevel, {
      ...payload,
      groupBy: ResourceManagementGroupBy.Users,
      includeSummaries: true,
      includeOverAllocations: true,
      includeEmptySummaries: true,
    }).then((x) => ({
      ...x,
      resources: x.resources.map((r) =>
        (r as any).user != null
          ? addUserTypeToAllocation(r as AllocationsGroupByUser)
          : addTeamTypeToAllocation(r as AllocationsGroupByTeam),
      ),
    }));

  public getResourceAllocationDemand = async (
    payload: GetResourceAllocationDemandPayload,
  ): Promise<ResourceAllocationDemandResponse> =>
    this.post(`${baseUrl}/demand`, payload);

  public bulkAction = async (
    payload: AllocationBulkPayload,
    requestId: string,
  ): Promise<any> =>
    this.post(`${baseUrl}/bulk-action`, payload, {
      headers: { 'X-Request-Id': requestId },
    });

  public getResourceAvailaibility(
    projectId: number,
    teamId: number,
  ): Promise<AvailabilityForTeamWithProject> {
    return this.post(
      `${baseUrl}/check-availability?projectId=${projectId}&teamId=${teamId}`,
    );
  }

  public findAvailableResources({
    startDate,
    endDate,
    availableMinutes,
    isHoursPerWeek,
  }: AvailabilityFilterApiWithPlanningLevel): Promise<number[]> {
    return this.post(`${baseUrl}/find-availability`, {
      availableMinutes,
      startDate: defaultFormat(startDate),
      endDate: defaultFormat(endDate),
      planningLevelConfig: isHoursPerWeek
        ? PlanningLevelConfig.HoursPerWeek
        : PlanningLevelConfig.HoursPerDay,
    });
  }

  public getFilteredResources = ({
    filter,
    groupBy,
  }: ResourceManagementFilterParams): Promise<ResourceManagementFilterResponse> => {
    // Doing this as for RM, BE needs role as role type instead of user type
    // in order to filter placeholders and replaced users as well
    const transformedFilterString = ApiGroupFilter.toQueryStringFormatForGroups(
      filter.groupCriteria,
    ).replace('user*role', 'role*role');

    return this.get(
      `${baseUrl}/filter-resources?${transformedFilterString}${
        filter.groupCriteria.length > 0 ? '&' : ''
      }${stringify(
        {
          groupBy,
          match: filter.groupCriteria.length ? filter.type : null,
        },
        { skipNull: true },
      )}`,
    );
  };

  public getFilteredResourcesInPeople = (
    filter: GroupedFilter,
  ): Promise<FilteredResponseInPeople> =>
    this.getFilteredResources({
      groupBy: ResourceManagementGroupBy.Users,
      filter,
    }).then((data) => ({
      peopleTabEntities: (
        data as FilteredResponseInPeople
      ).peopleTabEntities.map((x) =>
        (x as any).userId != null
          ? addUserTypeToFilteredResource(x as FilteredUserInPeople)
          : addRoleTypeToFilteredResource(x as FilteredRoleInPeople),
      ),
    }));

  public getFilteredResourcesInProjects = (
    filter: GroupedFilter,
  ): Promise<FilteredResponseInProjects> =>
    this.getFilteredResources({
      groupBy: ResourceManagementGroupBy.Projects,
      filter,
    }).then((data) => data as FilteredResponseInProjects);

  public deleteResourceAllocations = ({
    projectId,
    resourceIdentifier,
  }: DeleteAllocationsProps): Promise<void> =>
    this.delete(`${baseUrl}`, {
      data: {
        projectId,
        ...(resourceIdentifier.type === ResourceType.User
          ? {
              userId: resourceIdentifier.userId,
              roleId: resourceIdentifier.roleId,
              referenceTeamId: resourceIdentifier.referenceTeamId,
            }
          : {
              teamId: resourceIdentifier.teamId,
            }),
      },
    });

  public convertResourceAllocations = ({
    projectId,
    resourceIdentifier,
    medium,
  }: ConvertAllocationsPropsApi): Promise<void> =>
    this.put(`${baseUrl}`, {
      projectId,
      ...(resourceIdentifier
        ? resourceIdentifier.type === ResourceType.User
          ? {
              userId: resourceIdentifier.userId,
              roleId: resourceIdentifier.roleId,
              referenceTeamId: resourceIdentifier.referenceTeamId,
            }
          : {
              teamId: resourceIdentifier.teamId,
            }
        : {}),
      medium,
    });

  /** Resource Management Config */
  public getResourceManagementConfig =
    async (): Promise<ResourceManagementConfig> =>
      this.get(`${baseUrl}/configuration`);

  public updateResourceManagementConfig = async (
    payload: ResourceManagementConfig,
  ): Promise<ResourceManagementConfig> =>
    this.put(`${baseUrl}/configuration`, payload);

  public updateResourceManagementPlanningLevel = async (
    payload: string,
  ): Promise<ResourceManagementConfig> =>
    this.put(`${baseUrl}/configuration/update-planning-level`, payload, {
      headers: { 'Content-Type': 'text/plain' },
    });

  public getSourceTasksForAllocationId = async (
    allocationId: number,
  ): Promise<Task[]> =>
    this.get<unknown, { sources: AllocationSource[] }>(
      `${baseUrl}/${allocationId}/sources`,
    ).then((x) => x.sources.map((y) => y.metaData));

  public exportAllocations = async ({
    startDate,
    endDate,
    groupBy,
    zoomType,
    filter,
  }: ResourceManagementExportParams): Promise<string> =>
    this.get<{ downloadUrl: string }>(
      `/reports/resource-allocation/export?${ApiGroupFilter.toQueryStringFormatForGroups(
        filter.groupCriteria,
      )}${filter.groupCriteria.length > 0 ? '&' : ''}${stringify(
        {
          startDate: defaultFormat(startDate),
          endDate: defaultFormat(endDate),
          groupBy,
          zoomType,
          match: filter.groupCriteria.length ? filter.type : null,
        },
        { skipNull: true },
      )}`,
    ).then((response) => response.downloadUrl);
}

export const ResourceManagmentApi = new ResourceManagementClient();
